import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type Status = 'idle' | 'loading' | 'error';

export default function VisitaCTAForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) return;
    setStatus('loading');
    try {
      const body = new URLSearchParams();
      body.append('name', name);
      body.append('phone', phone);
      body.append('email', email);
      body.append('origin', `Blog - ${window.location.pathname}`);
      const res = await fetch('https://readdy.ai/api/form/d7kkgtq63r16okiqpnug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        navigate(`/obrigado${slug ? `?from=${slug}` : ''}`);
        return;
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="my-12 relative overflow-hidden rounded-2xl bg-[#1A1A1A]">
      {/* Background texture */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            'repeating-linear-gradient(45deg, #E8610A 0, #E8610A 1px, transparent 0, transparent 50%)',
          backgroundSize: '12px 12px',
        }}
      />
      <div className="relative z-10 p-8 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {/* Left: copy */}
            <div>
              <span className="inline-block bg-[#E8610A]/20 text-[#E8610A] text-xs font-semibold px-3 py-1 rounded-full mb-4 uppercase tracking-widest">
                Serviço gratuito
              </span>
              <h3
                className="text-white text-2xl md:text-3xl font-bold leading-tight mb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Quer uma visita técnica gratuita?
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-5">
                Um especialista da Home Office Premium vai até sua empresa, avalia o espaço e apresenta
                um projeto personalizado — <strong className="text-gray-200">sem compromisso</strong>.
              </p>
              <ul className="space-y-2">
                {[
                  'Diagnóstico ergonômico completo',
                  'Planta baixa e sugestão de layout',
                  'Orçamento sem enrolação',
                ].map(item => (
                  <li key={item} className="flex items-center gap-2 text-gray-300 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-[#E8610A] text-base"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: form */}
            <form
              data-readdy-form
              id="form-visita-tecnica-blog"
              onSubmit={handleSubmit}
              className="space-y-3"
            >
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="Seu nome *"
                  required
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full bg-white/10 border border-white/10 text-white placeholder-gray-500 text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-[#E8610A]/60 transition-colors"
                />
              </div>
              <div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="WhatsApp / Telefone *"
                  required
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  className="w-full bg-white/10 border border-white/10 text-white placeholder-gray-500 text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-[#E8610A]/60 transition-colors"
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="E-mail (opcional)"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full bg-white/10 border border-white/10 text-white placeholder-gray-500 text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-[#E8610A]/60 transition-colors"
                />
              </div>
              {status === 'error' && (
                <p className="text-red-400 text-xs">Erro ao enviar. Tente novamente.</p>
              )}
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full bg-[#E8610A] hover:bg-[#C4500A] disabled:opacity-60 text-white font-semibold text-sm py-3.5 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                {status === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Enviando...
                  </>
                ) : (
                  <>
                    <i className="ri-calendar-check-line"></i>
                    Quero a visita técnica gratuita
                  </>
                )}
              </button>
              <p className="text-gray-600 text-xs text-center">
                Seus dados são usados apenas para agendamento. Sem spam.
              </p>
            </form>
          </div>
      </div>
    </div>
  );
}
